import { useEffect, useRef } from 'react';
import { useToleranceStore } from '../stores/toleranceStore';
import { useProjectStore } from '../stores/projectStore';
import { useAuthStore, selectIsPro } from '../stores/authStore';
import { createAutoSaver } from '../lib/autoSaver';
import { toast } from '../lib/toast';

// Wait this long after the last edit before persisting. Short enough
// that "Oops, browser crashed" loses at most a few seconds; long
// enough that we don't hammer the API while the user is mid-edit.
const DEBOUNCE_MS = 3000;
// Retry delays after a failed auto-save; the last one repeats.
const BACKOFF_MS = [10_000, 30_000, 60_000];

// Live conditions, read from the stores at call time so timers never
// act on a stale render: Pro plan, project picked, calc already saved
// once (we don't auto-create; the first save is an explicit decision
// via "Speichern" or "+ Neue Berechnung"), and something to save.
function canAutoSave() {
  const { currentProject, currentCalculation } = useProjectStore.getState();
  return selectIsPro(useAuthStore.getState())
    && !!currentProject
    && !!currentCalculation
    && !useToleranceStore.getState().preview;
}
function hasPendingChanges() {
  return canAutoSave() && useToleranceStore.getState().isDirty;
}

/**
 * Auto-saves the current calculation for Pro users with a project
 * calculation open. A failed save is never silent (2026-10-13 a
 * token error swallowed changes, only the yellow dot showed it):
 * the header chip turns red via projectStore.saveError, one sticky
 * toast offers "Erneut versuchen", and the saver retries after
 * 10 s / 30 s / 60 s and whenever the tab becomes visible again.
 * Closing the tab with pending changes asks for confirmation.
 *
 * Mount once at the top of the calculator route.
 */
export function useAutoSave() {
  const isPro = useAuthStore(selectIsPro);
  const isDirty = useToleranceStore(s => s.isDirty);
  const currentProject = useProjectStore(s => s.currentProject);
  const currentCalculation = useProjectStore(s => s.currentCalculation);

  const saverRef = useRef(null);
  const toastIdRef = useRef(-1);

  useEffect(() => {
    const saver = createAutoSaver({
      save: () => useProjectStore.getState().saveCurrentCalculation(),
      shouldSave: hasPendingChanges,
      debounceMs: DEBOUNCE_MS,
      backoffMs: BACKOFF_MS,
      onFailure: (err, attempt) => {
        console.warn('Auto-Save fehlgeschlagen:', err?.message);
        if (attempt !== 1) return;
        toastIdRef.current = toast.error(
          `Automatisches Speichern fehlgeschlagen (${err?.message || 'unbekannter Fehler'}). Deine Änderungen sind noch nicht in der Cloud. Die App versucht es weiter.`,
          { duration: 0, action: { label: 'Erneut versuchen', onClick: () => saver.retryNow() } }
        );
      }
    });
    saverRef.current = saver;

    // Any successful save (auto or manual) ends the failure streak.
    const unsubscribe = useProjectStore.subscribe((state, prev) => {
      if (prev.saveError && !state.saveError) {
        toast.dismiss(toastIdRef.current);
        toastIdRef.current = -1;
        saver.cancel();
        if (hasPendingChanges()) saver.schedule();
      }
    });

    // Back from a locked screen or another tab: try right away.
    const onVisible = () => {
      if (document.visibilityState === 'visible' && saver.failures > 0) saver.retryNow();
    };
    const onBeforeUnload = (e) => {
      if (!hasPendingChanges()) return;
      e.preventDefault();
      e.returnValue = '';
    };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      unsubscribe();
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('beforeunload', onBeforeUnload);
      saver.dispose();
      toast.dismiss(toastIdRef.current);
      saverRef.current = null;
    };
  }, []);

  useEffect(() => {
    const saver = saverRef.current;
    if (!saver) return;
    if (!isPro || !currentProject || !currentCalculation) {
      // Calc closed or switched: the retry loop and its toast end here.
      saver.cancel();
      toast.dismiss(toastIdRef.current);
      toastIdRef.current = -1;
      return;
    }
    if (isDirty) saver.schedule();
  }, [isPro, isDirty, currentProject, currentCalculation]);
}
